import React, { useCallback, useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';
import { useAppLocale } from '../../providers/LocaleProvider';
import { useTheme } from '../../hooks/useTheme';
import { darkenColor } from '../../utils/colorUtils';
import { mirrorLinearGradientAngle, mirrorRtlDecor } from '../../utils/mirrorRtlDecor';
import {
  DASHBOARD_BOOK_CARD_WATERMARK,
  dashboardBookCardWatermarkCornerClass,
} from '../dashboard/dashboardCoursePanelStyles';

/** Inner panel gradient — dashboard hero violet theme (mirrors home FeaturePanelCard layout). */
const DASHBOARD_HERO_INNER_GRADIENT_LIGHT: [string, string, string] = ['#f5f3ff', '#fae8ff', '#eef2ff'];
const DASHBOARD_HERO_INNER_GRADIENT_DARK: [string, string, string] = ['#2e1065', '#581c87', '#3730a3'];

const DASHBOARD_HERO_INNER_RADIAL_A =
  'pointer-events-none absolute inset-0 rounded-[inherit] bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(167,139,250,0.18),transparent_58%)] dark:bg-[radial-gradient(ellipse_75%_55%_at_100%_0%,rgba(139,92,246,0.12),transparent_58%)]';

const DASHBOARD_HERO_INNER_RADIAL_B =
  'pointer-events-none absolute inset-0 rounded-[inherit] bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(192,132,252,0.1),transparent_55%)] dark:bg-[radial-gradient(ellipse_65%_50%_at_0%_100%,rgba(124,58,237,0.08),transparent_55%)]';

const DASHBOARD_HERO_BLOB_TOP =
  'pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-white/35 dark:bg-white/10';

const DASHBOARD_HERO_BLOB_BOTTOM =
  'pointer-events-none absolute -bottom-0 -left-8 h-28 w-28 rounded-full bg-violet-900/[0.06] dark:bg-black/25';

/** Outer clay frame — violet-tinted variant of home `.clay-card` (white on FeaturePanelCard). */
const DASHBOARD_HERO_CLAY_FRAME =
  'border border-violet-300/95 bg-violet-100/95 shadow-[0_8px_32px_rgba(139,92,246,0.14),inset_0_2px_4px_rgba(255,255,255,0.6),inset_0_-2px_4px_rgba(124,58,237,0.08)] ring-1 ring-violet-200/90 dark:border-violet-600/65 dark:bg-violet-950/80 dark:shadow-[0_8px_32px_rgba(0,0,0,0.35),inset_0_2px_4px_rgba(255,255,255,0.07),inset_0_-2px_4px_rgba(0,0,0,0.28)] dark:ring-violet-800/50';

export interface LearnerPageHeroProps {
  eyebrow: string;
  title: string;
  subtitle: React.ReactNode;
  icon: LucideIcon;
  /** Optional trailing control (e.g. CTA or back link). */
  action?: React.ReactNode;
  /** `top` places the action as the first row inside the hero. `side` keeps it beside the title. */
  actionPlacement?: 'side' | 'top';
  /** Optional control stacked under the leading icon (e.g. compact back). */
  belowIcon?: React.ReactNode;
  /** Slightly smaller logo on course, course details, and profile. */
  compactChrome?: boolean;
  className?: string;
  /** Matches `data-tour-id` for product tours. */
  tourId?: string;
}

function HeroBody({
  eyebrow,
  title,
  subtitle,
  icon: Icon,
  action,
  belowIcon,
  isRtl = false,
  compactChrome = false,
}: {
  eyebrow: string;
  title: string;
  subtitle: React.ReactNode;
  icon: LucideIcon;
  action?: React.ReactNode;
  belowIcon?: React.ReactNode;
  isRtl?: boolean;
  compactChrome?: boolean;
}) {
  const rtlDecor = (className: string) => mirrorRtlDecor(className, isRtl);

  return (
    <div className="relative z-[1] flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
      <div className="flex min-w-0 items-start gap-3 text-start">
        <div className="flex shrink-0 flex-col items-center gap-2">
          <div
            className={rtlDecor(
              compactChrome
                ? 'flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-indigo-600 shadow-md ring-2 ring-white/70 dark:from-violet-600 dark:via-fuchsia-600 dark:to-indigo-700 dark:ring-white/10 md:h-10 md:w-10'
                : 'flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-indigo-600 shadow-md ring-2 ring-white/70 dark:from-violet-600 dark:via-fuchsia-600 dark:to-indigo-700 dark:ring-white/10 md:h-12 md:w-12'
            )}
          >
            <Icon className={compactChrome ? 'h-4 w-4 text-white md:h-5 md:w-5' : 'h-5 w-5 text-white md:h-6 md:w-6'} aria-hidden />
          </div>
          {belowIcon ? <div className="flex w-full justify-center">{belowIcon}</div> : null}
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-violet-700/90 dark:text-violet-300/90 md:text-[11px]">
            {eyebrow}
          </p>
          <h1 className="mb-0.5 text-xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white md:text-2xl">
            {title}
          </h1>
          <div className="mme-hint text-xs leading-relaxed text-slate-600 dark:text-slate-400 md:text-sm">{subtitle}</div>
        </div>
      </div>
      {action ? <div className="w-full shrink-0 md:w-auto">{action}</div> : null}
    </div>
  );
}

/** Top-of-page hero for learner dashboard, courses, profile, and similar views. */
export function LearnerPageHero({
  eyebrow,
  title,
  subtitle,
  icon: Icon,
  action,
  actionPlacement = 'side',
  belowIcon,
  compactChrome = false,
  className = '',
  tourId,
}: LearnerPageHeroProps) {
  const { direction } = useAppLocale();
  const { isDark } = useTheme();
  const isRtl = direction === 'rtl';
  const rtlDecor = (className: string) => mirrorRtlDecor(className, isRtl);

  const makeGradient = useCallback(
    (...stops: string[]) => {
      if (!stops.length) return undefined;
      const adjusted = stops.map((stop) => (isDark ? darkenColor(stop, 0.35) : stop));
      const angle = mirrorLinearGradientAngle(135, isRtl);
      return `linear-gradient(${angle}deg, ${adjusted.join(', ')})`;
    },
    [isDark, isRtl]
  );

  const heroInnerGradient = useMemo(() => {
    const stops = isDark ? DASHBOARD_HERO_INNER_GRADIENT_DARK : DASHBOARD_HERO_INNER_GRADIENT_LIGHT;
    const ordered = isRtl ? ([...stops].reverse() as typeof stops) : stops;
    return makeGradient(...ordered);
  }, [isDark, isRtl, makeGradient]);

  return (
    <header
      className={`learner-page-hero group relative flex flex-col overflow-hidden rounded-2xl p-1 sm:p-1.5 ${DASHBOARD_HERO_CLAY_FRAME} ${className}`}
      dir={direction}
      aria-label={title}
      {...(tourId ? { 'data-tour-id': tourId } : {})}
    >
      <div
        className="relative isolate flex flex-1 flex-col overflow-hidden rounded-xl"
        style={{ background: heroInnerGradient }}
      >
        <div className={rtlDecor(DASHBOARD_HERO_BLOB_TOP)} aria-hidden />
        <div className={rtlDecor(DASHBOARD_HERO_BLOB_BOTTOM)} aria-hidden />
        <div className={rtlDecor(DASHBOARD_HERO_INNER_RADIAL_A)} aria-hidden />
        <div className={rtlDecor(DASHBOARD_HERO_INNER_RADIAL_B)} aria-hidden />
        <Icon
          className={`${DASHBOARD_BOOK_CARD_WATERMARK} text-violet-400 dark:text-violet-300 ${dashboardBookCardWatermarkCornerClass(isRtl)}`}
          strokeWidth={1.25}
          aria-hidden
        />
        <div className="relative p-4 md:p-5">
          {actionPlacement === 'top' && action ? (
            <div className="relative z-[1] mb-3 flex">{action}</div>
          ) : null}
          <HeroBody
            eyebrow={eyebrow}
            title={title}
            subtitle={subtitle}
            icon={Icon}
            action={actionPlacement === 'top' ? undefined : action}
            belowIcon={belowIcon}
            isRtl={isRtl}
            compactChrome={compactChrome}
          />
        </div>
      </div>
    </header>
  );
}
